import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPathChangeType } from '@/utils/uiCacheHelpers';

/**
 * Custom hook for managing filter options caching during navigation
 * @param loading Current loading state
 * @param filters Current filters
 * @param selectedOptions Current selected options
 * @returns Objects with effective filters, options and shimmer state
 */
export var useFilterCache = function useFilterCache(loading, filters, selectedOptions) {
  var location = useLocation();

  // State management
  var _useState = useState(''),
    currentPath = _useState[0],
    setCurrentPath = _useState[1];
  var _useState2 = useState(false),
    isPathChanging = _useState2[0],
    setIsPathChanging = _useState2[1];
  var _useState3 = useState(false),
    isShowingShimmer = _useState3[0],
    setIsShowingShimmer = _useState3[1];
  var _useState4 = useState(loading),
    prevLoading = _useState4[0],
    setPrevLoading = _useState4[1];
  var _useState5 = useState([]),
    cachedOptions = _useState5[0],
    setCachedOptions = _useState5[1];
  var _useState6 = useState([]),
    cachedFilters = _useState6[0],
    setCachedFilters = _useState6[1];

  // Track path changes to detect navigation between makes
  useEffect(function () {
    var newPath = location.pathname;
    if (currentPath && newPath !== currentPath) {
      var _getPathChangeType = getPathChangeType(currentPath, newPath),
        isSignificantChange = _getPathChangeType.isSignificantChange;
      if (isSignificantChange) {
        setIsPathChanging(true);
        setCachedOptions([]);
        setIsShowingShimmer(true);
      } else {
        setIsPathChanging(false);
      }
    }
    setCurrentPath(newPath);
  }, [location.pathname]);

  // Reset shimmer state after loading completes
  useEffect(function () {
    if (!loading && isShowingShimmer) {
      setIsShowingShimmer(false);
    }
  }, [loading]);

  // Store selected options when they're available - but only if we're not changing paths
  useEffect(function () {
    if (selectedOptions && selectedOptions.length > 0 && !isPathChanging) {
      setCachedOptions(selectedOptions);
    }
  }, [selectedOptions, isPathChanging]);

  // Store filters when they're available
  useEffect(function () {
    if (filters && filters.length > 0 && !isPathChanging) {
      setCachedFilters(filters);
    }
  }, [filters, isPathChanging]);

  // Update loading state
  useEffect(function () {
    setPrevLoading(loading);
  }, [loading]);

  // Use cached filters if available during loading, but not during path changes
  var effectiveFilters = loading && !isPathChanging && cachedFilters.length > 0 ? cachedFilters : filters;
  var effectiveOptions = loading && !isPathChanging && cachedOptions.length > 0 ? cachedOptions : selectedOptions;

  // Determine if we should show shimmer during path changes
  var shouldShowShimmer = isShowingShimmer || loading && isPathChanging;
  return {
    effectiveFilters: effectiveFilters,
    effectiveOptions: effectiveOptions,
    shouldShowShimmer: shouldShowShimmer,
    isPathChanging: isPathChanging
  };
};
export default useFilterCache;